import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  PurchaseOrder, 
  PurchaseOrderListParams, 
  PurchaseOrderFormData,
  purchaseOrderService 
} from '@/services/purchaseOrder/purchaseOrderService';

interface PurchaseOrderState {
  purchaseOrders: PurchaseOrder[];
  selectedPurchaseOrder: PurchaseOrder | null;
  purchaseOrdersLoading: boolean;
  selectedPurchaseOrderLoading: boolean;
  isLoading: boolean;
  error: string | null;
  pagination: {
    totalDocs: number;
    limit: number;
    totalPages: number;
    page: number;
    pagingCounter: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
  } | null;
}

const initialState: PurchaseOrderState = {
  purchaseOrders: [],
  selectedPurchaseOrder: null,
  purchaseOrdersLoading: false,
  selectedPurchaseOrderLoading: false,
  isLoading: false,
  error: null,
  pagination: null,
};

export const fetchPurchaseOrders = createAsyncThunk(
  'purchaseOrder/fetchPurchaseOrders',
  async (params: PurchaseOrderListParams) => {
    const response = await purchaseOrderService.getPurchaseOrders(params);
    return response.data;
  }
);

export const createPurchaseOrder = createAsyncThunk(
  'purchaseOrder/createPurchaseOrder',
  async (data: PurchaseOrderFormData) => {
    const response = await purchaseOrderService.createPurchaseOrder(data);
    return response.data;
  }
);

export const updatePurchaseOrder = createAsyncThunk(
  'purchaseOrder/updatePurchaseOrder',
  async ({ id, data }: { id: string; data: PurchaseOrderFormData }) => {
    const response = await purchaseOrderService.updatePurchaseOrder(id, data);
    return response.data;
  }
);

export const deletePurchaseOrder = createAsyncThunk(
  'purchaseOrder/deletePurchaseOrder',
  async (id: string) => {
    await purchaseOrderService.deletePurchaseOrder(id);
    return id;
  }
);

export const fetchPurchaseOrderById = createAsyncThunk(
  'purchaseOrder/fetchPurchaseOrderById',
  async (id: string) => {
    const response = await purchaseOrderService.getPurchaseOrderById(id);
    return response.data;
  }
);

export const purchaseOrderSlice = createSlice({
  name: 'purchaseOrder',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchaseOrders.pending, (state) => {
        state.purchaseOrdersLoading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseOrders.fulfilled, (state, action) => {
        state.purchaseOrdersLoading = false;
        state.purchaseOrders = action.payload.docs;
        const { docs: _, ...pagination } = action.payload;
        state.pagination = pagination;
      })
      .addCase(fetchPurchaseOrders.rejected, (state, action) => {
        state.purchaseOrdersLoading = false;
        state.error = action.error.message || 'Failed to fetch purchase orders';
      })
      .addCase(createPurchaseOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPurchaseOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.purchaseOrders.unshift(action.payload);
      })
      .addCase(createPurchaseOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to create purchase order';
      })
      .addCase(updatePurchaseOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePurchaseOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.purchaseOrders.findIndex(po => po._id === action.payload._id);
        if (index !== -1) {
          state.purchaseOrders[index] = action.payload;
        }
      })
      .addCase(updatePurchaseOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update purchase order';
      })
      .addCase(deletePurchaseOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePurchaseOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.purchaseOrders = state.purchaseOrders.filter(po => po._id !== action.payload);
      })
      .addCase(deletePurchaseOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to delete purchase order';
      })
      .addCase(fetchPurchaseOrderById.pending, (state) => {
        state.selectedPurchaseOrderLoading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseOrderById.fulfilled, (state, action) => {
        state.selectedPurchaseOrderLoading = false;
        state.selectedPurchaseOrder = action.payload;
      })
      .addCase(fetchPurchaseOrderById.rejected, (state, action) => {
        state.selectedPurchaseOrderLoading = false;
        state.error = action.error.message || 'Failed to fetch purchase order details';
      });
  },
});

export const { clearError } = purchaseOrderSlice.actions;
export default purchaseOrderSlice.reducer;